import {
  Box,
  Collapse,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  useMediaQuery
} from "@material-ui/core";
import AppBar from "@material-ui/core/AppBar";
import { grey } from "@material-ui/core/colors";
import CssBaseline from "@material-ui/core/CssBaseline";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import Drawer from "@material-ui/core/Drawer";
import Hidden from "@material-ui/core/Hidden";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme
} from "@material-ui/core/styles";
import Toolbar from "@material-ui/core/Toolbar";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import {
  AccountBalance,
  AttachMoney,
  Delete,
  ExpandLess,
  ExpandMore,
  PieChartOutlined,
  RecordVoiceOver
} from "@material-ui/icons";
import AssignmentIcon from "@material-ui/icons/Assignment";
import AssignmentTurnedInOutlinedIcon from "@material-ui/icons/AssignmentTurnedInOutlined";
import BarChartIcon from "@material-ui/icons/BarChart";
import CloseIcon from "@material-ui/icons/Close";
import DateRangeIcon from "@material-ui/icons/DateRange";
import FastfoodIcon from "@material-ui/icons/Fastfood";
import GroupIcon from "@material-ui/icons/Group";
import HomeIcon from "@material-ui/icons/Home";
import InboxIcon from "@material-ui/icons/Inbox";
import MenuIcon from "@material-ui/icons/Menu";
import RedeemIcon from "@material-ui/icons/Redeem";
import { Loyalty, StoreOutlined } from "@mui/icons-material";
import LayersIcon from "@mui/icons-material/Layers";
import PaidIcon from "@mui/icons-material/Paid";
import SettingsIcon from "@mui/icons-material/Settings";
import StoreIcon from "@mui/icons-material/Store";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Chart } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { useContext, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Authorized } from "./Authorized";
import { permissions, whiteLabelCampaignRoles } from "./constants";
import { useI18n } from "./hooks/useI18n";
import MainContent from "./MainContent";
import {
  FeatureType,
  productCampaignsHiddenTenantTypes,
  TenantTypeName
} from "./models/Tenant";
import { hasRole } from "./models/User";
import { UserContext } from "./UserProvider";
import ChainMobilePOSControl from "./views/tenantChains";
import { MyStoreMenu } from "./views/toolbar/mystore/MyStoreMenu";
import { TenantMenu } from "./views/toolbar/tenants/TenantMenu";
import { UserMenu } from "./views/toolbar/usersettings/UserMenu";
const drawerWidth = 280;

Chart.register([annotationPlugin]);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex"
    },
    drawer: {
      [theme.breakpoints.up("md")]: {
        width: drawerWidth,
        flexShrink: 0
      }
    },
    appBar: {
      "@media print": {
        display: "none"
      },
      minHeight: 40,
      zIndex: 1201 //dialogs have 1300 and they need to be in front
    },
    menuButton: {
      marginRight: theme.spacing(2),
      [theme.breakpoints.up("md")]: {
        display: "none"
      }
    },
    navLink: {
      "&.active .activeMark": {
        position: "absolute",
        height: "100%",
        width: "0.3rem",
        left: 0
      }
    },
    // necessary for content to be below app bar
    drawerPaper: {
      width: drawerWidth
    },
    content: {
      flexGrow: 1,
      overflowY: "auto",
      overflowX: "hidden",
      backgroundColor: grey["50"],
      [theme.breakpoints.up("md")]: {
        paddingTop: theme.spacing(3),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        backgroundColor: theme.palette.success
      }
    },

    nested: {
      paddingLeft: theme.spacing(4)
    },
    field: {
      display: "flex",
      justifyContent: "center",
      marginBottom: theme.spacing(2),
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2)
    },
    text: {
      fontWeight: 500,
      fontSize: "1rem",
      [theme.breakpoints.down("sm")]: {
        fontSize: "0.85rem"
      },
      marginLeft: theme.spacing(0.5)
    },
    menuText: {
      fontSize: "0.75rem"
    },
    toolbar: {
      minHeight: 40,
      [theme.breakpoints.up("sm")]: {
        minHeight: 40
      }
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500]
    },
    headerItem: {
      color: "white",
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(1.5)
    },
    headerButton: {
      color: "white",
      margin: theme.spacing(0, 0.5)
    },
    icon: {
      // Extend your existing icon styles,
      marginRight: "8px",
      fontSize: "1.5rem",
      [theme.breakpoints.down("sm")]: {
        fontSize: "1.2rem"
      }
    }
  })
);

interface MenuItem {
  name: string;
  path?: string;
  icon: any;
  tenantTypeNames: TenantTypeName[];
  allow?: string[];
  feature?: FeatureType;
  nested?: MenuItem[];
}

const getMenuItems = (t: any): ReadonlyArray<MenuItem> => [
  {
    name: t("common.overview"),
    path: "/",
    icon: <HomeIcon />,
    allow: permissions.overview,
    tenantTypeNames: [
      TenantTypeName.StoreKey,
      TenantTypeName.WhiteLabel,
      TenantTypeName.MobilePOS
    ]
  },
  {
    name: t("common.products"),
    path: "/tenant-products",
    icon: <FastfoodIcon />,
    allow: permissions.products,
    tenantTypeNames: [TenantTypeName.StoreKey]
  },
  {
    name: t("common.products"),
    path: "/tenant-products",
    icon: <FastfoodIcon />,
    allow: permissions.productCampaigns,
    tenantTypeNames: [...productCampaignsHiddenTenantTypes]
  },
  {
    name: t("common.thingsToDo"),
    icon: <AssignmentTurnedInOutlinedIcon />,
    allow: permissions.stores,
    tenantTypeNames: [TenantTypeName.StoreKey, TenantTypeName.WhiteLabel],
    nested: [
      {
        name: t("common.orders"),
        path: "/stores/all/orders",
        feature: "centralOrderingSystem",
        icon: <DateRangeIcon />,
        allow: permissions.stores,
        tenantTypeNames: [TenantTypeName.StoreKey, TenantTypeName.WhiteLabel]
      },
      {
        name: t("common.clickNCollect"),
        path: "/stores/any/user-orders",
        feature: "clickNCollect",
        icon: <InboxIcon />,
        allow: permissions.stores,
        tenantTypeNames: [TenantTypeName.StoreKey, TenantTypeName.WhiteLabel]
      }
    ]
  },

  {
    name: t("common.segments"),
    path: "/segments",
    icon: <PieChartOutlined />,
    allow: permissions.marketing,
    tenantTypeNames: [TenantTypeName.StoreKey, TenantTypeName.WhiteLabel]
  },
  {
    name: t("common.campaigns.title"),
    path: "/campaigns",
    icon: <RedeemIcon />,
    allow: permissions.stores,
    tenantTypeNames: [TenantTypeName.StoreKey]
  },
  {
    name: t("common.campaigns.title"),
    path: "/campaigns",
    icon: <RedeemIcon />,
    allow: whiteLabelCampaignRoles,
    tenantTypeNames: [TenantTypeName.WhiteLabel]
  },
  {
    name: t("common.campaigns.title"),
    path: "/campaigns",
    icon: <RedeemIcon />,
    allow: permissions.productCampaigns,
    tenantTypeNames: [TenantTypeName.MobilePOS]
  },
  {
    name: t("common.receipts"),
    path: "/receipts",
    icon: <AttachMoney />,
    allow: permissions.stores,
    tenantTypeNames: [TenantTypeName.MobilePOS]
  },
  {
    name: t("common.cashierReports"),
    path: "/cashier-reports",
    icon: <AssignmentIcon />,
    allow: permissions.stores,
    tenantTypeNames: [TenantTypeName.MobilePOS]
  },
  {
    name: t("common.coupons"),
    path: "/discounts",
    icon: <Loyalty />,
    allow: permissions.root,
    tenantTypeNames: [TenantTypeName.StoreKey, TenantTypeName.WhiteLabel]
  },
  {
    name: t("common.salesAndVisitors"),
    path: "/sales-and-visitors",
    icon: <LayersIcon />,
    allow: permissions.users,
    tenantTypeNames: [TenantTypeName.StoreKey, TenantTypeName.WhiteLabel]
  },
  {
    name: t("common.navigation.allStoreVisitors"),
    path: "/users",
    icon: <GroupIcon />,
    allow: permissions.users,
    tenantTypeNames: [TenantTypeName.StoreKey, TenantTypeName.WhiteLabel]
  },
  {
    name: t("common.navigation.statistics"),
    icon: <BarChartIcon />,
    allow: [...permissions.statistics, ...permissions.public],
    tenantTypeNames: [TenantTypeName.StoreKey, TenantTypeName.WhiteLabel],
    nested: [
      {
        name: t("common.navigation.payouts"),
        path: "/statistics/payouts",
        icon: <PaidIcon />,
        allow: [...permissions.statistics, ...permissions.public],
        tenantTypeNames: [TenantTypeName.StoreKey, TenantTypeName.WhiteLabel]
      },
      {
        name: t("common.navigation.sales"),
        path: "/statistics/sales",
        icon: <AttachMoney />,
        allow: [...permissions.statistics, ...permissions.public],
        tenantTypeNames: [TenantTypeName.StoreKey, TenantTypeName.WhiteLabel]
      },
      {
        name: t("common.navigation.profit"),
        path: "/statistics/profit",
        icon: <AccountBalance />,
        allow: [...permissions.statistics, ...permissions.public],
        tenantTypeNames: [TenantTypeName.StoreKey, TenantTypeName.WhiteLabel]
      },
      {
        name: t("common.navigation.waste"),
        path: "/statistics/waste",
        icon: <Delete />,
        allow: [...permissions.statistics, ...permissions.public],
        tenantTypeNames: [TenantTypeName.StoreKey, TenantTypeName.WhiteLabel]
      },
      {
        name: t("common.navigation.campaign"),
        path: "/statistics/campaign",
        icon: <RedeemIcon />,
        allow: permissions.statistics,
        tenantTypeNames: [TenantTypeName.StoreKey, TenantTypeName.WhiteLabel]
      },
      {
        name: t("common.navigation.customerFeedback"),
        path: "/statistics/feedback",
        icon: <RecordVoiceOver />,
        allow: permissions.statistics,
        tenantTypeNames: [TenantTypeName.StoreKey, TenantTypeName.WhiteLabel]
      }
    ]
  },
  {
    name: t("common.navigation.reports"),
    path: "/reports",
    icon: <AssignmentIcon />,
    allow: permissions.reports,
    tenantTypeNames: [TenantTypeName.StoreKey, TenantTypeName.WhiteLabel]
  }
];

interface MenuToggleItem {
  name: string;
  open: boolean;
}

export default function ResponsiveApp() {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useI18n();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const locationState = location.state as any;
  const background = locationState && locationState?.background;

  const menuItems = getMenuItems(t);
  const [menuToggles, setMenuToggles] = useState<MenuToggleItem[]>(
    menuItems
      .filter(item => item.nested)
      .map(item => {
        const obj: MenuToggleItem = {
          name: item.name,
          open: false
        };
        return obj;
      })
  );

  const [showChainDialog, setShowChainDialog] = useState(false);
  const [showStoreSelectDialog, setShowStoreSelectDialog] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isNestedMenuOpen = (menuItem: MenuItem): boolean => {
    const found =
      menuToggles.length && menuToggles.find(x => x.name === menuItem.name);
    return !found || found.open;
  };

  const toggleNestedMenu = (menuItem: MenuItem) => {
    let index = menuToggles.findIndex(x => x.name === menuItem.name);

    menuToggles[index].open = !menuToggles[index].open;

    setMenuToggles([...menuToggles]);
  };

  const user = useContext(UserContext);

  if (user.type === "ANONYMOUS") {
    return null;
  }

  const { name } = user.tenant;

  const [queryClient] = useState(() => new QueryClient());

  const drawer = (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <div className={classes.toolbar} />
      <Divider />
      <div style={{ flex: "1 0 auto" }}>
        <List>
          {menuItems.map(item => (
            <Authorized
              key={item.name}
              allow={item.allow}
              feature={item.feature}
              tenantTypeNames={item.tenantTypeNames}
            >
              {item.nested &&
              item.nested.some(element => Authorized(element)) ? (
                <>
                  <ListItem button onClick={() => toggleNestedMenu(item)}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText
                      className={classes.menuText}
                      primary={item.name}
                    />

                    {isNestedMenuOpen(item) ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Divider />
                  <Collapse
                    in={isNestedMenuOpen(item)}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {item.nested.map(nestedItem => (
                        <Authorized
                          key={nestedItem.name}
                          allow={nestedItem.allow}
                          feature={nestedItem.feature}
                          tenantTypeNames={nestedItem.tenantTypeNames}
                        >
                          <div key={nestedItem.name} role="presentation">
                            <ListItem
                              button
                              className={`${classes.nested} ${classes.navLink}`}
                              component={NavLink}
                              onClick={() => {
                                if (mobileOpen) {
                                  handleDrawerToggle();
                                }
                              }}
                              end={true}
                              to={nestedItem.path ?? ""}
                            >
                              <span
                                className="activeMark"
                                style={{
                                  fontSize: "0.85rem",
                                  backgroundColor: theme.palette.primary.main
                                }}
                              />
                              <ListItemIcon>{nestedItem.icon}</ListItemIcon>
                              <ListItemText
                                className={classes.menuText}
                                primary={nestedItem.name}
                              />
                            </ListItem>
                            <Divider />
                          </div>
                        </Authorized>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : !item.nested ? (
                <div role="presentation">
                  <ListItem
                    button
                    key={item.name}
                    className={classes.navLink}
                    component={NavLink}
                    onClick={() => {
                      if (mobileOpen) {
                        handleDrawerToggle();
                      }
                    }}
                    to={item.path ?? ""}
                    end={true}
                  >
                    <span
                      className="activeMark"
                      style={{
                        backgroundColor: theme.palette.primary.main
                      }}
                    />
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText
                      className={classes.menuText}
                      primary={item.name}
                    />
                  </ListItem>
                  <Divider />
                </div>
              ) : null}
            </Authorized>
          ))}
        </List>
      </div>
    </div>
  );

  useEffect(() => {
    document.title = name ?? t("common.navigation.administration");
  });

  const [reloadTenantState, setReloadTenantState] = useState(false);

  useEffect(() => {
    if (reloadTenantState) {
      window.location.reload();
    }
  }, [reloadTenantState]);

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            color="inherit"
            aria-label={t("common.tooltip.openDrawer")}
            edge="start"
            onClick={handleDrawerToggle}
            className={classes.menuButton}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          {user.tenant.tenantType.name === TenantTypeName.MobilePOS ? (
            <div className={classes.headerItem}>
              <StoreOutlined className={classes.icon} />
              <Typography
                variant="subtitle2"
                component="span"
                className={classes.text}
              >
                {(() => {
                  const customerId =
                    (user.tenant as any)?.customerId ??
                    (user.tenant as any)?.CustomerId;
                  return customerId
                    ? `${customerId} - ${user.tenant.name}`
                    : user.tenant.name;
                })()}
              </Typography>
            </div>
          ) : (
            <QueryClientProvider client={queryClient}>
              <MyStoreMenu
                dialogBarStyle={{
                  backgroundColor: "#26449d"
                }}
              />
            </QueryClientProvider>
          )}

          {(hasRole(user, ["Root"]) || user?.tenantIds?.length > 1) && (
            <IconButton
              className={classes.headerButton}
              onClick={() => setShowStoreSelectDialog(true)}
              aria-label={t("common.tooltip.selectStore")}
            >
              <StoreIcon className={classes.icon} />
              {!isMobile && (
                <Typography
                  variant="subtitle2"
                  component="span"
                  className={classes.text}
                >
                  {t("common.tooltip.selectStore")}
                </Typography>
              )}
            </IconButton>
          )}

          {hasRole(user, ["Root", "ITAdmin", "ITSupport", "ChainSupport"]) && (
            <IconButton
              className={classes.headerButton}
              onClick={() => setShowChainDialog(true)}
              aria-label={t("common.tooltip.selectChain")}
            >
              <SettingsIcon className={classes.icon} />
              {!isMobile && (
                <Typography
                  variant="subtitle2"
                  component="span"
                  className={classes.text}
                >
                  {t("common.tooltip.selectChain")}
                </Typography>
              )}
            </IconButton>
          )}

          {/* <LanguageSwitcher /> */}

          <Tooltip title={t("common.user")} style={{ color: "white" }}>
            <UserMenu
              user={user}
              dialogBarStyle={{
                backgroundColor: "#26449d",
                position: "relative"
              }}
            />
          </Tooltip>
        </Toolbar>
      </AppBar>
      <nav className={classes.drawer}>
        <Hidden mdUp implementation="css">
          <Drawer
            style={{ zIndex: 1199, fontSize: "0.85rem" }}
            variant="temporary"
            anchor={theme.direction === "rtl" ? "right" : "left"}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            classes={{
              paper: classes.drawerPaper
            }}
            ModalProps={{
              keepMounted: true
            }}
          >
            {drawer}
          </Drawer>
        </Hidden>
        <Hidden smDown implementation="css">
          <Drawer
            classes={{
              paper: classes.drawerPaper
            }}
            variant="permanent"
            open
          >
            {drawer}
          </Drawer>
        </Hidden>
      </nav>
      <MainContent queryClient={queryClient} />

      <Dialog
        fullWidth
        maxWidth={"md"}
        open={showChainDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle>
          {t("common.mobilePosActivation")}
          <IconButton
            aria-label={t("common.tooltip.close")}
            className={classes.closeButton}
            onClick={() => {
              setShowChainDialog(false);
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent dividers>
          <QueryClientProvider client={queryClient}>
            <ChainMobilePOSControl />
          </QueryClientProvider>
        </DialogContent>
      </Dialog>

      <QueryClientProvider client={queryClient}>
        <TenantMenu
          open={showStoreSelectDialog}
          onClose={reload => {
            if (reload) {
              setReloadTenantState(true);
            }
            setShowStoreSelectDialog(false);
          }}
        />
      </QueryClientProvider>
    </div>
  );
}
